import * as React from 'react';
import { Alert, Flex, FlexItem, FormSelect, FormSelectOption, InputGroup, InputGroupText, Text, TextInput } from '@patternfly/react-core';
import { InnerScrollContainer, TableComposable, Thead, Tr, Th, Tbody, Td } from '@patternfly/react-table';
import CoopPriceProjectionInterface from '@app/Interface/CoopPriceProjectionInterface';
import WeightedAverageInterface from '@app/Interface/WeightedAverageInterface';

interface ProjectedMilkValueCalculatorProps {
    coopPriceProjections: CoopPriceProjectionInterface[];
    weightedAverage: WeightedAverageInterface|null;
}

const parseToFixed = (number: number|string, digits: number = 2) => {
    const float = typeof number === 'string' 
        ? Number.parseFloat(number)
        : number;
    
    if (Number.isNaN(float)) {
        return 0;
    }

    return float.toFixed(digits);
};

const getDefaultProjection = (projections: CoopPriceProjectionInterface[]) => {
    const currentDate = new Date();
    const currentProjection = projections.find(
        projection => Number(projection.PricingMonth) === (currentDate.getMonth() + 1) 
                      && Number(projection.PricingYear) === currentDate.getFullYear()
    );

    return currentProjection ?? projections[0];
};

const ProjectedMilkValueCalculator: React.FC<ProjectedMilkValueCalculatorProps> = ({ coopPriceProjections, weightedAverage }) => {
    const defaultProjection = getDefaultProjection(coopPriceProjections);
    // Projection date state
    const [selectedProjection, setSelectedProjection] = React.useState<CoopPriceProjectionInterface>(defaultProjection);
    // Production state
    const [productionProjectedLbs, setProductionProjectedLbs] = React.useState(weightedAverage?.Weight ?? 0);
    const [productionPriceProjection, setProductionPriceProjection] = React.useState(parseToFixed(defaultProjection.PriceCWT, 4));
    // Butterfat state
    const [bfPercent, setBfPercent] = React.useState(parseToFixed(weightedAverage?.Fat ?? 0));
    const [bfPriceProjection, setBfPriceProjection] = React.useState(parseToFixed(defaultProjection.BFat, 4));
    // Protein state
    const [protPercent, setProtPercent] = React.useState(parseToFixed(weightedAverage?.Protein ?? 0));
    const [protPriceProjection, setProtPriceProjection] = React.useState(parseToFixed(defaultProjection.Protein, 4));
    // Other Solids state
    const [oSolPercent, setOSolPercent] = React.useState(parseToFixed(weightedAverage?.OtherSolids ?? 0));
    const [oSolPriceProjection, setOSolPriceProjection] = React.useState(parseToFixed(defaultProjection.OSol, 4));

    
    // Reset "Your Projections" field values when new projection is selected
    React.useEffect(() => {
        setProductionPriceProjection(
            parseToFixed(selectedProjection.PriceCWT, 4)
        );
        setBfPriceProjection(
            parseToFixed(selectedProjection.BFat, 4)
        );
        setProtPriceProjection(
            parseToFixed(selectedProjection.Protein, 4)
        );
        setOSolPriceProjection(
            parseToFixed(selectedProjection.OSol, 4)
        );
    }, [selectedProjection]);

    // Calculate values for fields non populated by user or selected projection
    // Calculations pulled from https://bitbucket.org/sitecrafting/nda-solution-original/src/156b30b747f4708342037133659b4dad1fea53be/MyNDA/Reports/ProjectValue.aspx#lines-240
    const calculatedValues = (() => {
        const perFat = Number(bfPercent) * 0.1;
        const perProt = Number(protPercent) * 0.1;
        const perOsol = Number(oSolPercent) * 0.1;
        const projLbs = Number(productionProjectedLbs) * 0.1;

        const projFat = Number(bfPriceProjection);
        const projProt = Number(protPriceProjection);
        const projOsol = Number(oSolPriceProjection);
        const projCwt = Number(productionPriceProjection);

        const fatLbs = perFat * projLbs;
        const protLbs = perProt * projLbs;
        const osolLbs = perOsol * projLbs;

        const fatTotal = (fatLbs * projFat * 100);
        const protTotal = (protLbs * projProt * 100);
        const osolTotal = (osolLbs * projOsol * 100);
        const lbsTotal = ((projLbs / 10) * projCwt * 100);

        const grossTotal = fatTotal + protTotal + osolTotal + lbsTotal;
        let cwtTotal = grossTotal / projLbs;
        if (Number.isNaN(cwtTotal)) {
            cwtTotal = 0;
        }
        return {
            bfProjectedLbs: fatLbs.toLocaleString('en-US', {maximumFractionDigits:0}),
            protProjectedLbs: protLbs.toLocaleString('en-US', {maximumFractionDigits:0}),
            oSolProjectedLbs: osolLbs.toLocaleString('en-US', {maximumFractionDigits:0}),
            productionProjectedTotal: (lbsTotal/100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits:2}),
            bfProjectedTotal: (fatTotal/100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits:2}),
            protProjectedTotal: (protTotal/100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits:2}),
            oSolProjectedTotal: (osolTotal/100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits:2}),
            totalValueCwt: ((cwtTotal*10)/100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits:2}),
            totalGrossValue: (grossTotal/100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits:2}),
        };
    })();

    return (
        <>
            {weightedAverage === null &&
                <Alert 
                    variant="warning" 
                    title="No month to date data available for selected FPU"
                    className="pf-u-mb-lg"
                    role="alert"
                >
                    <p>
                        This calculator relies on month to date data, but none is available for the selected FPU. As a result, some default values in the calculator have been set to 0.
                    </p>
                </Alert>
            }
            <Flex className="pf-u-mb-lg">
                <FlexItem>
                    <Text>
                        Projected for: 
                    </Text>
                </FlexItem>
                <FlexItem>
                    <FormSelect 
                        aria-label="Pricing projected for"
                        value={selectedProjection.ID}
                        onChange={newSelectedProjectionId => {
                            const newSelectedProjection = coopPriceProjections.find(
                                projection => Number(projection.ID) === Number(newSelectedProjectionId)
                            );
                            setSelectedProjection(newSelectedProjection ?? coopPriceProjections[0]);
                        }}
                    >
                        {coopPriceProjections.map(projection =>
                            <FormSelectOption 
                                key={projection.ID} 
                                value={projection.ID} 
                                label={`${projection.PricingMonth}/${projection.PricingYear}`}
                            />
                        )}
                    </FormSelect>
                </FlexItem>
            </Flex>

            <InnerScrollContainer>
                <TableComposable
                    variant="compact"
                    borders={true}
                >
                    <Thead>
                        <Tr>
                            <Th modifier="nowrap" width={10}></Th>
                            <Th modifier="nowrap" width={20}>Component %</Th>
                            <Th modifier="nowrap" width={20}>Projected lbs</Th>
                            <Th modifier="nowrap" width={20}>Price Projections</Th>
                            <Th modifier="nowrap" width={20}>Your Projections</Th>
                            <Th modifier="nowrap" width={20}>Projected Total</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        <Tr>
                            <Td modifier="nowrap">Butterfat</Td>
                            <Td modifier="nowrap" dataLabel="Component %">
                                <InputGroup style={{minWidth: '140px'}}>
                                    <TextInput 
                                        min="0"
                                        max="100"
                                        value={bfPercent} 
                                        onChange={value => setBfPercent(value)}
                                        type="number" 
                                        aria-label="Butterfat component percent" 
                                    />
                                    <InputGroupText aria-label="Percent">%</InputGroupText>
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Projected lbs">
                                <InputGroup style={{minWidth: '150px'}}>
                                    <TextInput 
                                        type="text" 
                                        value={calculatedValues.bfProjectedLbs}
                                        aria-label="Butterfat projected pounds" 
                                        isDisabled 
                                    />
                                    <InputGroupText aria-label="Pounds">lbs</InputGroupText>
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Price Projections">
                                <InputGroup style={{minWidth: '140px'}}>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        value={parseToFixed(selectedProjection.BFat, 4)} 
                                        type="number" 
                                        aria-label="Butterfat price projections" 
                                        isDisabled 
                                    />
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Your Projections">
                                <InputGroup style={{minWidth: '140px'}}>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        value={bfPriceProjection}
                                        onChange={value => setBfPriceProjection(value)}
                                        type="number" 
                                        aria-label="Butterfat your projections" 
                                    />
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Projected Total">
                                <InputGroup style={{minWidth: '150px'}}>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        type="text" 
                                        value={calculatedValues.bfProjectedTotal}
                                        aria-label="Butterfat projected total" 
                                        isDisabled 
                                    />
                                </InputGroup>
                            </Td>
                        </Tr>
                        <Tr>
                            <Td modifier="nowrap">Protein</Td>
                            <Td modifier="nowrap" dataLabel="Component %">
                                <InputGroup>
                                    <TextInput 
                                        min="0"
                                        max="100"
                                        value={protPercent} 
                                        onChange={value => setProtPercent(value)}
                                        type="number" 
                                        aria-label="Protein component percent" 
                                    />
                                    <InputGroupText aria-label="Percent">%</InputGroupText>
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Projected lbs">
                                <InputGroup>
                                    <TextInput 
                                        type="text" 
                                        value={calculatedValues.protProjectedLbs}
                                        aria-label="Protein projected pounds" 
                                        isDisabled 
                                    />
                                    <InputGroupText aria-label="Pounds">lbs</InputGroupText>
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Price Projections">
                                <InputGroup>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        value={parseToFixed(selectedProjection.Protein, 4)}
                                        type="number" 
                                        aria-label="Protein price projections" 
                                        isDisabled 
                                    />
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Your Projections">
                                <InputGroup>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        value={protPriceProjection}
                                        onChange={value => setProtPriceProjection(value)}
                                        type="number" 
                                        aria-label="Protein your projections" 
                                    />
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Projected Total">
                                <InputGroup>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        type="text" 
                                        value={calculatedValues.protProjectedTotal}
                                        aria-label="Protein projected total" 
                                        isDisabled 
                                    />
                                </InputGroup>
                            </Td>
                        </Tr>
                        <Tr>
                            <Td modifier="nowrap">Other Solids</Td>
                            <Td modifier="nowrap" dataLabel="Component %">
                                <InputGroup>
                                    <TextInput 
                                        min="0"
                                        max="100"
                                        value={oSolPercent} 
                                        onChange={value => setOSolPercent(value)}
                                        type="number" 
                                        aria-label="Other Solids component percent" 
                                    />
                                    <InputGroupText aria-label="Percent">%</InputGroupText>
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Projected lbs">
                                <InputGroup>
                                    <TextInput 
                                        type="text" 
                                        value={calculatedValues.oSolProjectedLbs}
                                        aria-label="Other Solids projected pounds" 
                                        isDisabled 
                                    />
                                    <InputGroupText aria-label="Pounds">lbs</InputGroupText>
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Price Projections">
                                <InputGroup>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        value={parseToFixed(selectedProjection.OSol, 4)}
                                        type="number" 
                                        aria-label="Other Solids price projections" 
                                        isDisabled 
                                    />
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Your Projections">
                                <InputGroup>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        value={oSolPriceProjection}
                                        onChange={value => setOSolPriceProjection(value)}
                                        type="number" 
                                        aria-label="Other Solids your projections" 
                                    />
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Projected Total">
                                <InputGroup>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        type="text" 
                                        value={calculatedValues.oSolProjectedTotal}
                                        aria-label="Other Solids projected total" 
                                        isDisabled 
                                    />
                                </InputGroup>
                            </Td>
                        </Tr>
                        <Tr>
                            <Td modifier="nowrap">Production</Td>
                            <Td modifier="nowrap"></Td>
                            <Td modifier="nowrap" dataLabel="Projected lbs">
                                <InputGroup>
                                    <TextInput 
                                        value={productionProjectedLbs} 
                                        onChange={value => setProductionProjectedLbs(Number(value))}
                                        type="number" 
                                        aria-label="Production projected pounds" 
                                    />
                                    <InputGroupText aria-label="Pounds">lbs</InputGroupText>
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Price Projections">
                                <InputGroup>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        value={parseToFixed(selectedProjection.PriceCWT, 4)}
                                        type="number" 
                                        aria-label="Production price projections" 
                                        isDisabled 
                                    />
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Your Projections">
                                <InputGroup>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        value={productionPriceProjection}
                                        onChange={value => setProductionPriceProjection(value)}
                                        type="number" 
                                        aria-label="Production your projections" 
                                    />
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap" dataLabel="Projected Total">
                                <InputGroup>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        type="text" 
                                        value={calculatedValues.productionProjectedTotal}
                                        aria-label="Production projected total" 
                                        isDisabled 
                                    />
                                </InputGroup>
                            </Td>
                        </Tr>
                        <Tr>
                            <Td modifier="nowrap" dataLabel="Totals"></Td>
                            <Td modifier="nowrap"></Td>
                            <Td modifier="nowrap"><strong>Value/CWT</strong></Td>
                            <Td modifier="nowrap">
                                <InputGroup>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        type="text" 
                                        value={calculatedValues.totalValueCwt}
                                        aria-label="Projected value per CWT" 
                                        isDisabled 
                                    />
                                </InputGroup>
                            </Td>
                            <Td modifier="nowrap"><strong>Gross Value</strong></Td>
                            <Td modifier="nowrap">
                                <InputGroup>
                                    <InputGroupText aria-label="Dollars">$</InputGroupText>
                                    <TextInput 
                                        type="text" 
                                        value={calculatedValues.totalGrossValue}
                                        aria-label="Projected gross value" 
                                        isDisabled 
                                    />
                                </InputGroup>
                            </Td>
                        </Tr>
                    </Tbody>
                </TableComposable>
            </InnerScrollContainer>
        </>
    )

};

export default ProjectedMilkValueCalculator;